import { ActionIcon, Group, Modal, MultiSelect, ScrollArea, Stack, Table, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { IconDeviceFloppy, IconPlus, IconTrash } from '@tabler/icons-react'
import type { GradeOption, GroupOption } from 'api/arcMosaico/types'
import { Button } from 'components/Button'
import { AddGroupModal } from '../../TeacherDashboard/components/ReviewStudentRequests/AddGroupModal'
import { useArcTranslation } from 'hooks'
import { useState } from 'react'

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

interface FacultyRow {
  _id: string
  firstName: string
  lastName: string
  email: string
  gradeIds: string[]
  groupIds: string[]
}

let rowCounter = 0
const emptyRow = (): FacultyRow => ({
  _id: `row-${++rowCounter}`,
  firstName: '',
  lastName: '',
  email: '',
  gradeIds: [],
  groupIds: [],
})

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const inputErrorStyles = {
  root: { position: 'relative' as const, paddingBottom: 20 },
  error: { position: 'absolute' as const, bottom: 0 },
}

const isRowTouched = (row: FacultyRow) =>
  !!(
    row.firstName.trim() ||
    row.lastName.trim() ||
    row.email.trim() ||
    row.gradeIds.length > 0 ||
    row.groupIds.length > 0
  )

interface Props {
  opened: boolean
  grades: GradeOption[]
  groups: GroupOption[]
  onClose: () => void
  onSave: (rows: FacultyRow[]) => Promise<void>
  onAddGroup: (name: string) => Promise<GroupOption>
}

export function AddFacultyModal({ opened, grades, groups, onClose, onSave, onAddGroup }: Props) {
  const { t } = useArcTranslation()
  const [loading, setLoading] = useState(false)
  const [addGroupOpened, setAddGroupOpened] = useState(false)
  const [addGroupLoading, setAddGroupLoading] = useState(false)

  const form = useForm({
    initialValues: {
      rows: [emptyRow()],
    },
    validateInputOnBlur: true,
    validate: {
      rows: {
        firstName: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.rows[index]
          if (!isRowTouched(row)) return null
          return !value?.trim() ? t('add_faculty_modal.validation_first_name_required') : null
        },
        lastName: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.rows[index]
          if (!isRowTouched(row)) return null
          return !value?.trim() ? t('add_faculty_modal.validation_last_name_required') : null
        },
        email: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.rows[index]
          if (!isRowTouched(row)) return null
          if (!value?.trim()) return t('add_faculty_modal.validation_email_required')
          if (!EMAIL_REGEX.test(value)) return t('add_faculty_modal.validation_email_invalid')
          return null
        },
        groupIds: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.rows[index]
          if (!isRowTouched(row)) return null
          return value.length === 0 ? t('add_faculty_modal.validation_group_required') : null
        },
        gradeIds: (value, values, path) => {
          const index = Number(path.split('.')[1])
          const row = values.rows[index]
          if (!isRowTouched(row)) return null
          return value.length === 0 ? t('add_faculty_modal.validation_grade_required') : null
        },
      },
    },
  })

  const gradeOptions = grades
    .map(g => ({ value: g.id, label: g.name }))
    .sort((a, b) => naturalSort(a.label, b.label))

  const groupOptions = [
    { value: '__create__', label: `+ ${t('student_requests.create_group')}` },
    ...groups
      .map(g => ({ value: g.id, label: g.name }))
      .sort((a, b) => naturalSort(a.label, b.label)),
  ]

  const handleAddGroup = async (name: string): Promise<boolean> => {
    setAddGroupLoading(true)
    try {
      await onAddGroup(name)
      return true
    } catch {
      return false
    } finally {
      setAddGroupLoading(false)
    }
  }

  const handleGroupChange = (index: number, values: string[]) => {
    if (values.includes('__create__')) {
      setAddGroupOpened(true)
      return
    }
    handleFieldChange(index, 'groupIds', values)
  }

  const handleFieldChange = (index: number, field: string, value: string | string[]) => {
    form.setFieldValue(`rows.${index}.${field}`, value)
    const rows = form.values.rows
    const isLastRow = index === rows.length - 1
    const hasValue = typeof value === 'string' ? value.trim().length > 0 : value.length > 0
    if (isLastRow && hasValue) {
      form.insertListItem('rows', emptyRow())
    }
  }

  const addRow = () => form.insertListItem('rows', emptyRow())

  const removeRow = (index: number) => {
    if (form.values.rows.length <= 1) return
    form.removeListItem('rows', index)
  }

  const resetAndClose = () => {
    form.reset()
    onClose()
  }

  const handleClose = () => {
    if (form.isDirty()) {
      modals.openConfirmModal({
        title: t('add_faculty_modal.confirm_discard_title'),
        children: <Text size='sm'>{t('add_faculty_modal.confirm_discard')}</Text>,
        labels: {
          confirm: t('add_faculty_modal.discard'),
          cancel: t('add_faculty_modal.keep_editing'),
        },
        confirmProps: { color: 'red' },
        centered: true,
        onConfirm: resetAndClose,
      })
      return
    }
    resetAndClose()
  }

  const handleSave = async () => {
    const validation = form.validate()
    if (validation.hasErrors) return

    const validRows = form.values.rows.filter(r => isRowTouched(r))
    if (validRows.length === 0) return

    setLoading(true)
    try {
      await onSave(validRows)
      resetAndClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
    <Modal opened={opened} onClose={handleClose} title={t('add_faculty_modal.title')} size='80%' centered>
      <Stack spacing='md'>
        <ScrollArea>
          <Table
            sx={{
              'thead tr th, tbody tr, tbody tr td': { borderBottom: 'none', borderTop: 'none' },
              'tbody tr td': { verticalAlign: 'top' },
            }}
          >
            <thead>
              <tr>
                <th>{t('add_faculty_modal.first_name')}</th>
                <th>{t('add_faculty_modal.last_name')}</th>
                <th>{t('add_faculty_modal.email')}</th>
                <th>{t('add_faculty_modal.group')}</th>
                <th>{t('add_faculty_modal.grade')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {form.values.rows.map((row, index) => (
                <tr key={row._id}>
                  <td>
                    <TextInput
                      placeholder={t('add_faculty_modal.first_name_placeholder')}
                      {...form.getInputProps(`rows.${index}.firstName`)}
                      onChange={e => handleFieldChange(index, 'firstName', e.currentTarget.value)}
                      styles={inputErrorStyles}
                      size='sm'
                    />
                  </td>
                  <td>
                    <TextInput
                      placeholder={t('add_faculty_modal.last_name_placeholder')}
                      {...form.getInputProps(`rows.${index}.lastName`)}
                      onChange={e => handleFieldChange(index, 'lastName', e.currentTarget.value)}
                      styles={inputErrorStyles}
                      size='sm'
                    />
                  </td>
                  <td>
                    <TextInput
                      placeholder={t('add_faculty_modal.email_placeholder')}
                      {...form.getInputProps(`rows.${index}.email`)}
                      onChange={e => handleFieldChange(index, 'email', e.currentTarget.value)}
                      styles={inputErrorStyles}
                      size='sm'
                    />
                  </td>
                  <td>
                    <MultiSelect
                      data={groupOptions}
                      {...form.getInputProps(`rows.${index}.groupIds`)}
                      onChange={val => handleGroupChange(index, val)}
                      placeholder={t('add_faculty_modal.select_group')}
                      size='sm'
                      withinPortal
                      searchable
                      styles={{ ...inputErrorStyles, root: { ...inputErrorStyles.root, minWidth: 160 } }}
                    />
                  </td>
                  <td>
                    <MultiSelect
                      data={gradeOptions}
                      {...form.getInputProps(`rows.${index}.gradeIds`)}
                      onChange={val => handleFieldChange(index, 'gradeIds', val)}
                      placeholder={t('add_faculty_modal.select_grade')}
                      size='sm'
                      withinPortal
                      styles={{ ...inputErrorStyles, root: { ...inputErrorStyles.root, minWidth: 160 } }}
                    />
                  </td>
                  <td>
                    <ActionIcon
                      color='red'
                      variant='subtle'
                      onClick={() => removeRow(index)}
                      disabled={form.values.rows.length <= 1}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollArea>

        <Button variant='subtle' leftIcon={<IconPlus size={16} />} onClick={addRow} sx={{ alignSelf: 'flex-start' }}>
          {t('add_faculty_modal.add_more')}
        </Button>

        <Group position='right'>
          <Button variant='default' onClick={handleClose}>
            {t('add_faculty_modal.cancel')}
          </Button>
          <Button color='turquoise' leftIcon={<IconDeviceFloppy size={16} />} onClick={handleSave} loading={loading}>
            {t('add_faculty_modal.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>

    <AddGroupModal
      opened={addGroupOpened}
      onClose={() => setAddGroupOpened(false)}
      onSave={handleAddGroup}
      loading={addGroupLoading}
    />
    </>
  )
}
